import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, Link } from 'react-router-dom'

import Waiting from './Waiting';
import PageTitle from './PageTitle';
import ErrorDisplay from './ErrorDisplay';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import useMediaQuery from '@mui/material/useMediaQuery';
import useStyles from '../styles/Styles'

import _ from 'lodash';
import { isTrue, maybeWithPrefix } from '../utils';
import { describeTutor, describeVenue, outputField, getCourseSchedule } from '../utils/courseUtilities';
import { fetchCourse, loadCoursesInfo, loadSettings, loadEnrolmentsSettings} from '../actions';

import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const CourseSingle = () => {
	
	const { courseCode } = useParams();

	const dispatch = useDispatch();
	const generalSettings = useSelector(state => state.settings.general);
	const course = useSelector(state => state.courses.singles[courseCode]);
	const fetchingCourse = useSelector(state => _.includes(state.courses.fetching_singles, courseCode));

	const user = useSelector(state => state.user);

	const coursesSettings = useSelector(state => state.settings.courses.data);
	const courseCategories = useSelector(state => state.settings.course_categories.data);
	const courseFrequencies = useSelector(state => state.settings.course_frequencies.data);
	const courseTypes = useSelector(state => state.settings.course_types.data);
	const courseTutors = useSelector(state => state.settings.tutors.data);
	const courseVenues = useSelector(state => state.settings.venues.data);
	const courseLabels = useSelector(state => state.settings.labels_courses.data);
	const enrolmentsSettings = useSelector(state => state.settings.enrolments.data);
	
	const message = useSelector(state => state.cart.message);

	const classes = useStyles();
	const isMd = useMediaQuery('(min-width:960px)');

	// courseCode isn't going to change - not a dependency
	useEffect(() => {
		if(generalSettings.expires > 0){
			dispatch(fetchCourse(courseCode)); 
			dispatch(loadSettings('courses'));
			// This also dispatches enrolments request if required
			dispatch(loadEnrolmentsSettings(true));
			dispatch(loadCoursesInfo('general')); 
		} // eslint-disable-next-line
	}, [dispatch, generalSettings]);
	
	useEffect(() => {
		if(!isTrue(coursesSettings.hide_tutor_from_public) || user.member_no > 0){
			dispatch(loadCoursesInfo('tutors'));
		}
	}, [dispatch, coursesSettings, user]);
	
	const [fieldNames, setFieldNames] = useState({
		code: "Course code", 
		category_code: "Category", 
		type_code: "Course type",
		detail: 'Detail', 
		additional_detail: 'Additional information', 
		tutor: "Tutor",
		tutor2: "",
		venue_code: "Venue",
		fee1: "Full fee", 
	});
	
	useEffect(() => {
		setFieldNames(f => { return {...f, 
			code: courseLabels.courseCapLabel + " code", 
			category_code: courseLabels.category, 
			type_code: courseLabels.courseCapLabel + " type",
			tutor: courseLabels.tutor, 
			tutor2: courseLabels.tutor2, 
			venue_code: courseLabels.venue, 
			fee1: courseLabels.fee1
		}});

	}, [courseLabels]);
	
	const [displayFields, setDisplayFields] = useState({
		tutor: false,
		tutor2: false,
		additional_detail: false
	});
	
	useEffect(() => {
		setDisplayFields( f => { return {...f, 
			tutor: user.member_no > 0 || !isTrue(coursesSettings.hide_tutor_from_public),
			tutor2: courseLabels.tutor2 !== '' && (user.member_no > 0 || !isTrue(coursesSettings.hide_tutor_from_public)),
			additional_detail: coursesSettings.show_additional_detail
		}}); // eslint-disable-next-line
	}, [courseLabels, coursesSettings]);
	
	const [submitting, setSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState(false);

	// errorMessage is not a dependency; only want to run this when submitting or message change
	useEffect(() => {
		if(submitting && !_.isEmpty(message)){
			setSubmitting(false);
			setErrorMessage(message);
		} else if(!submitting && !_.isEmpty(errorMessage) && _.isEmpty(message)){
			setErrorMessage(false);
		} // eslint-disable-next-line
	}, [submitting, message])
	
	const getCourseDetail = (course) => {
		const category_description = _.has(courseCategories, course.category_code) ? courseCategories[course.category_code].description : course.category_code;
		const type_desciption = _.has(courseTypes, course.type_code) ? courseTypes[course.type_code].description : course.type_code;
		
		const tutor = courseTutors[course.tutor] || null;
		const tutor2 = courseTutors[course.tutor2] || null;
		const venue = courseVenues[course.venue_code] || null;
		const frequency = courseFrequencies[course.frequency_code] || null;
		
		let describeCourseAvailability = 'No';
		
		if(!enrolmentsSettings.general_enrol_on && !enrolmentsSettings.early_enrol_on){
			describeCourseAvailability = 'No: All enrolments are currently off';
		} else if(isTrue(course.is_finished)){
			describeCourseAvailability = 'No: ' + courseLabels.courseCapLabel + ' is finished';
		} else if(dayjs(course.start_date).isSameOrBefore(coursesSettings.member_courses_end_date) &&
				dayjs(course.end_date).isSameOrAfter(coursesSettings.member_courses_start_date)){
			if(isTrue(course.is_closed)){
				describeCourseAvailability = 'No: ' + courseLabels.courseCapLabel + ' is closed to enrolments';
			} else {
				if(enrolmentsSettings.early_enrol_on){
					describeCourseAvailability = 'Yes: Early enrolments only';
				} else if(course.available < 1) {
					describeCourseAvailability = 'Yes: Waitlist enrolments only';
				} else {
					describeCourseAvailability = 'Yes';
				}
			}
		}
		
		const courseAvailability = <>
		Accepting enrolments? {describeCourseAvailability}<br/>
		Capacity: {course.max_no_student}, enrolled: {course.enrolled}, available: {course.available}, waitlisted: {course.waitlisted}
		</>;
	
		const venueDetail = <>{describeVenue(venue, course.venue_code)} {isTrue(course.distance_course) ? <><br/><strong>This is a distance {courseLabels.courseLabel}</strong></> : null}</>;
		
		return (
		<CardContent>
		<Grid container className={classes.courseDetails}>
			{course.is_new ? outputField(<strong>New!</strong>, course.name +' is a new addition to the timetable', isMd, classes, 12) : null}
			{outputField(fieldNames.code, course.code, isMd, classes, 12)}
			{outputField(fieldNames.category_code, maybeWithPrefix(course.category_code, category_description), isMd, classes, 12)}
			{outputField(fieldNames.type_code, maybeWithPrefix(course.type_code, type_desciption ), isMd, classes, 12)}
			{outputField(fieldNames.detail, course.detail, isMd, classes, 12)}
			{displayFields.additional_detail ? outputField(fieldNames.additional_detail, course.additional_detail, isMd, classes, 12) : null}
			{displayFields.tutor ? outputField(fieldNames.tutor, describeTutor(tutor, course.tutor), isMd, classes, 12) : null}
			{displayFields.tutor2 ? outputField(fieldNames.tutor2, describeTutor(tutor2, course.tutor2), isMd, classes, 12) : null}
			{outputField(fieldNames.venue_code, venueDetail, isMd, classes, 12)}
			{outputField('Timetable', getCourseSchedule(course, frequency), isMd, classes, 12)}
			{outputField('Availability', courseAvailability, isMd, classes, 12)}
			{outputField(fieldNames.fee1, '$' + course.fee1, isMd, classes, 12)}
		</Grid>
		</CardContent>
	)}
	
	return (
	<>
	<ErrorDisplay />
	
	<Breadcrumbs separator="›" aria-label="breadcrumb" className={classes.otherContent}>
	<Link to="/courses" >{courseLabels.coursesCapLabel}</Link>
	<Typography>{courseCode}</Typography>
	</Breadcrumbs>
	<PageTitle title={course ? course.name : courseCode} withoutPaper />
	
	<Card 
	className={classes.innerContent} 
	square={true}>
	{(generalSettings.expires === 0 || fetchingCourse) ?
		<CardContent>
		<Waiting message={'Fetching ' + courseLabels.courseLabel + ' detail. Please wait...'} />
		</CardContent>:
		(_.isNil(course) ? <CardContent/> :
		<>
		{getCourseDetail(course)}
		</>
		
		)
	}
	</Card >
	</>
)};

export default CourseSingle;